/**
 * Tenant → Plans & Upgrade
 *
 * Three tabs:
 *   1. Compare      — side-by-side feature matrix + per-plan "Preview change" CTA
 *   2. How it works — pro-rata formulas + worked example + suggestion-engine rules
 *   3. History      — past upgrades/downgrades + pending cancellation
 *
 * Audit trail: every preview, initiate, cancel, applied event lands in audit_log.
 */
import { useCallback, useEffect, useState } from "react";
import { api, apiError } from "@/lib/api";
import { toast } from "sonner";
import { Check, X, Sparkles, TrendingUp, TrendingDown, Loader2, Info, History, BookOpen, Ban } from "lucide-react";
import Modal, { GoldButton, GhostButton, PageHeader } from "@/components/Ui";
import { useTableControls, SortableTh, FilterChip } from "@/components/SortableTable";

export default function BillingPlans() {
    const [tab, setTab] = useState("compare");
    const [data, setData] = useState(null);
    const [suggest, setSuggest] = useState(null);
    const [target, setTarget] = useState(null);
    const [proRata, setProRata] = useState(null);
    const [busy, setBusy] = useState(false);
    const [history, setHistory] = useState([]);
    const [logic, setLogic] = useState(null);

    const load = useCallback(async () => {
        try {
            const [c, s, h, l] = await Promise.all([
                api.get("/subscriptions/compare"),
                api.get("/subscriptions/suggest").catch(() => ({ data: { suggestions: [], metrics: {} } })),
                api.get("/subscriptions/changes").catch(() => ({ data: [] })),
                api.get("/subscriptions/logic").catch(() => ({ data: null })),
            ]);
            setData(c.data); setSuggest(s.data); setHistory(h.data || []); setLogic(l.data);
        } catch (e) { toast.error(apiError(e)); }
    }, []);
    useEffect(() => { load(); }, [load]);

    const openPreview = async (plan) => {
        setTarget(plan); setProRata(null);
        try {
            const { data } = await api.post("/subscriptions/preview-change", { target_plan_id: plan.id });
            setProRata(data);
        } catch (e) { toast.error(apiError(e)); setTarget(null); }
    };

    const confirmChange = async () => {
        setBusy(true);
        try {
            const { data } = await api.post("/subscriptions/change", { target_plan_id: target.id });
            if (data.action === "razorpay_checkout") {
                await new Promise((resolve, reject) => {
                    const s = document.createElement("script");
                    s.src = "https://checkout.razorpay.com/v1/checkout.js";
                    s.onload = resolve; s.onerror = reject;
                    document.body.appendChild(s);
                });
                // eslint-disable-next-line no-undef
                const rzp = new window.Razorpay({
                    key: data.key_id,
                    order_id: data.order_id,
                    amount: Math.round(data.amount * 100), currency: "INR",
                    name: "Law OS", description: `Upgrade to ${target.name}`,
                    handler: async () => {
                        toast.success("Payment received — plan upgrading…");
                        setTarget(null); setBusy(false); load();
                    },
                    modal: { ondismiss: () => setBusy(false) },
                    theme: { color: "#D4AF37" },
                });
                rzp.open();
            } else {
                toast.success("Downgrade scheduled for next renewal");
                setTarget(null); setBusy(false); load();
            }
        } catch (e) { toast.error(apiError(e)); setBusy(false); }
    };

    const cancelChange = async (id) => {
        if (!window.confirm("Cancel this scheduled change?")) return;
        try {
            await api.post(`/subscriptions/changes/${id}/cancel`);
            toast.success("Change cancelled");
            load();
        } catch (e) { toast.error(apiError(e)); }
    };

    if (!data) return <div className="p-6 text-sm text-muted-foreground">Loading plans…</div>;
    const suggestedIds = new Set((suggest?.suggestions || []).map(s => s.plan_id));
    const pendingDowngrade = history.find(h => h.status === "scheduled");

    const Tab = ({ id, icon: Icon, label }) => (
        <button onClick={() => setTab(id)} data-testid={`tab-${id}`}
            className={`px-4 py-2 text-xs uppercase tracking-[0.15em] flex items-center gap-2 border-b-2 ${tab === id ? "border-primary text-primary" : "border-transparent text-muted-foreground hover:text-foreground"}`}>
            <Icon className="w-3.5 h-3.5"/>{label}
        </button>
    );

    return (
        <>
            <PageHeader title="Plans & Upgrade" subtitle="Transparent pro-rata billing · Side-by-side feature comparison · Recommended for your usage."/>

            {pendingDowngrade && (
                <div className="border border-blue-700/40 bg-blue-950/20 p-3 mb-5 flex items-center justify-between text-xs" data-testid="pending-downgrade-banner">
                    <div className="text-blue-300">
                        <span className="uppercase tracking-[0.1em] mr-2">Scheduled downgrade</span>
                        {pendingDowngrade.from_plan} → {pendingDowngrade.to_plan} · takes effect at next renewal
                    </div>
                    <button onClick={() => cancelChange(pendingDowngrade.id)} data-testid="cancel-pending-downgrade"
                        className="px-3 py-1 border border-blue-700/40 text-blue-300 hover:bg-blue-950/40 text-[10px] uppercase tracking-[0.1em]">
                        <Ban className="w-3 h-3 inline mr-1"/>Cancel
                    </button>
                </div>
            )}

            {suggest?.suggestions?.length > 0 && (
                <div className="border border-primary/40 bg-primary/5 p-4 mb-6 flex items-start gap-3" data-testid="suggestion-banner">
                    <Sparkles className="w-4 h-4 text-primary mt-0.5 shrink-0"/>
                    <div className="text-xs flex-1">
                        <div className="text-primary font-medium mb-1">Recommended for you</div>
                        {suggest.suggestions.map((s, i) => (
                            <div key={`${s.plan_id}-${i}`} className="text-muted-foreground">→ <strong className="text-foreground">{s.plan_name}</strong> — {s.reason} <span className="text-muted-foreground">(triggered by {s.metric}={s.value} {s.op} {s.threshold})</span></div>
                        ))}
                    </div>
                </div>
            )}

            <div className="flex gap-1 border-b border-border mb-6 overflow-x-auto scrollbar-hide">
                <Tab id="compare" icon={Sparkles} label="Compare"/>
                <Tab id="how" icon={BookOpen} label="How it works"/>
                <Tab id="history" icon={History} label={`History${history.length ? ` (${history.length})` : ""}`}/>
            </div>

            {tab === "compare" && <CompareTab data={data} suggestedIds={suggestedIds} openPreview={openPreview}/>}
            {tab === "how" && <HowItWorksTab logic={logic} metrics={suggest?.metrics}/>}
            {tab === "history" && <HistoryTab items={history} cancelChange={cancelChange}/>}

            {target && (
                <Modal open={true} onClose={() => setTarget(null)} title={`Switch to ${target.name}`} size="lg" testId="prorata-modal">
                    {!proRata ? (
                        <div className="text-center py-8"><Loader2 className="w-6 h-6 animate-spin text-primary mx-auto"/></div>
                    ) : <ProRataBody proRata={proRata} busy={busy} confirmChange={confirmChange} cancel={() => setTarget(null)}/>}
                </Modal>
            )}
        </>
    );
}

function CompareTab({ data, suggestedIds, openPreview }) {
    return (
        <>
            <div className="grid md:grid-cols-3 gap-4 mb-6" data-testid="plans-grid">
                {data.plans.map(p => {
                    const isCustom = p.is_custom_pricing || p.price === null || p.price === undefined;
                    const isOneTime = p.kind === "founder_one_time" || p.kind === "one_time" || p.cycle === "one_time";
                    return (
                    <div key={p.id} className={`border p-5 flex flex-col ${suggestedIds.has(p.id) ? "border-primary bg-primary/5" : "border-border bg-card"}`} data-testid={`plan-card-${p.id}`}>
                        {suggestedIds.has(p.id) && (
                            <div className="text-[10px] uppercase tracking-[0.2em] text-primary mb-2 flex items-center gap-1"><Sparkles className="w-3 h-3"/>Recommended</div>
                        )}
                        <div className="text-lg font-serif">{p.name}</div>
                        {isCustom ? (
                            <div className="text-2xl text-primary mt-2" data-testid={`plan-price-${p.id}`}>Custom<span className="text-xs text-muted-foreground ml-1">/ contact sales</span></div>
                        ) : isOneTime ? (
                            <div className="text-2xl text-primary mt-2" data-testid={`plan-price-${p.id}`}>₹{Number(p.price || 0).toLocaleString("en-IN")}<span className="text-xs text-muted-foreground ml-1">one-time</span></div>
                        ) : (
                            <div className="text-2xl text-primary mt-2" data-testid={`plan-price-${p.id}`}>₹{Number(p.price || 0).toLocaleString("en-IN")}<span className="text-xs text-muted-foreground">/{String(p.cycle || "monthly").replace(/_/g, " ")}</span></div>
                        )}
                        <div className="text-[11px] text-muted-foreground mt-2 line-clamp-3 min-h-[3rem]">{p.description}</div>
                        <div className="text-[11px] text-muted-foreground mt-1">Up to {p.max_users} users</div>
                        <div className="flex-1"></div>
                        <button onClick={() => openPreview(p)} data-testid={`upgrade-${p.id}`}
                            className="w-full mt-4 py-2 border border-primary text-primary hover:bg-primary/10 text-xs uppercase tracking-[0.15em]">
                            {isCustom ? "Contact sales" : "Preview change"}
                        </button>
                    </div>
                );})}
            </div>

            <div className="border border-border overflow-x-auto" data-testid="feature-matrix">
                <table className="w-full text-sm">
                    <thead className="bg-muted">
                        <tr>
                            <th className="text-left px-4 py-3 text-xs uppercase font-medium tracking-wider">Feature</th>
                            {data.plans.map(p => <th key={p.id} className="text-center px-4 py-3 text-xs uppercase font-medium tracking-wider">{p.name}</th>)}
                        </tr>
                    </thead>
                    <tbody>
                        {data.matrix.map(row => (
                            <tr key={row.key} className="border-t border-border">
                                <td className="px-4 py-2 text-xs">
                                    <span className="text-muted-foreground text-[10px] uppercase block">{row.category}</span>
                                    {row.label}
                                </td>
                                {data.plans.map(p => (
                                    <td key={p.id} className="text-center px-4 py-2">
                                        {row[p.id] ? <Check className="w-4 h-4 text-emerald-400 inline" strokeWidth={2}/> : <X className="w-4 h-4 text-border inline" strokeWidth={1}/>}
                                    </td>
                                ))}
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        </>
    );
}

function ProRataBody({ proRata, busy, confirmChange, cancel }) {
    const isCustom = proRata.is_custom_pricing || proRata.requires_sales_contact;
    const isTrial = proRata.is_trial;
    const hasGst = (proRata.gst_pct || 0) > 0 && (proRata.gst_amount || 0) > 0;
    const banner = isCustom
        ? "CUSTOM PRICING — our team will contact you to finalize the agreement"
        : isTrial && proRata.is_upgrade
            ? "FIRST PAYMENT — trial → full plan price (no proration on trial)"
            : proRata.is_upgrade
                ? "UPGRADE — daily pro-rata, takes effect immediately"
                : "DOWNGRADE — monthly credit, takes effect at next renewal";
    return (
        <div className="space-y-4">
            <div className={`p-3 border rounded-lg ${isCustom ? "border-amber-700/40 bg-amber-950/20" : proRata.is_upgrade ? "border-emerald-700/40 bg-emerald-950/20" : "border-blue-700/40 bg-blue-950/20"} flex items-center gap-3`}>
                {isCustom ? <Info className="w-4 h-4 text-amber-400"/> : proRata.is_upgrade ? <TrendingUp className="w-4 h-4 text-emerald-400"/> : <TrendingDown className="w-4 h-4 text-blue-400"/>}
                <div className="text-xs">
                    <div className={isCustom ? "text-amber-300" : proRata.is_upgrade ? "text-emerald-300" : "text-blue-300"}>{banner}</div>
                </div>
            </div>

            <div className="border border-border divide-y divide-border rounded-lg overflow-hidden" data-testid="prorata-lines">
                {proRata.lines.map((l, i) => (
                    <div key={i} className="px-4 py-3 flex items-start justify-between text-sm">
                        <div className="flex-1">
                            <div className="text-foreground">{l.label}</div>
                            {l.calc && <div className="text-[10px] text-muted-foreground mt-1 font-mono">{l.calc}</div>}
                        </div>
                        <div className={`font-mono ${l.amount < 0 ? "text-emerald-400" : l.amount > 0 ? "text-foreground" : "text-muted-foreground"}`}>
                            {l.amount === 0 ? "—" : `${l.amount < 0 ? "−" : "+"}₹${Math.abs(l.amount).toLocaleString("en-IN", {minimumFractionDigits: 2})}`}
                        </div>
                    </div>
                ))}
                {proRata.is_upgrade && !isCustom && (
                    <>
                        <div className="px-4 py-2 flex justify-between text-xs text-muted-foreground">
                            <span>Subtotal</span>
                            <span className="font-mono">₹{proRata.subtotal.toLocaleString("en-IN", {minimumFractionDigits: 2})}</span>
                        </div>
                        {hasGst && (
                            <div className="px-4 py-2 flex justify-between text-xs text-muted-foreground">
                                <span>GST ({proRata.gst_pct}%)</span>
                                <span className="font-mono">₹{proRata.gst_amount.toLocaleString("en-IN", {minimumFractionDigits: 2})}</span>
                            </div>
                        )}
                        <div className="px-4 py-3 flex justify-between text-sm bg-primary/10">
                            <span className="text-primary uppercase tracking-[0.1em]">Pay now</span>
                            <span className="font-mono text-primary text-lg" data-testid="prorata-total">₹{proRata.amount_due_now.toLocaleString("en-IN", {minimumFractionDigits: 2})}</span>
                        </div>
                    </>
                )}
            </div>

            <div className="text-[10px] text-muted-foreground flex items-start gap-2">
                <Info className="w-3 h-3 mt-0.5 shrink-0"/>
                <div>
                    {isCustom ? (
                        <>Enterprise plans require a custom contract. Our team will reach out within 1 business day to discuss pricing, SLAs, and onboarding. Click <strong>Contact Sales</strong> below to send an email.</>
                    ) : isTrial ? (
                        <>You are currently on trial. No proration credit is given because trial days were free — you'll pay the full price for your first paid cycle.</>
                    ) : (
                        <>Current cycle: {proRata.days_elapsed} of {proRata.cycle_days} days used · {proRata.days_remaining} remaining. Current daily rate ₹{proRata.current_plan.daily_rate} · New daily rate ₹{proRata.target_plan.daily_rate}. Every preview & change is recorded in the audit log.</>
                    )}
                </div>
            </div>

            <div className="flex justify-end gap-2 pt-2 border-t border-border">
                <GhostButton onClick={cancel}>Cancel</GhostButton>
                {isCustom ? (
                    <a
                        href={`mailto:sales@lawos.in?subject=Enterprise%20plan%20inquiry%20-%20${encodeURIComponent(proRata.target_plan?.name || "Enterprise")}&body=Hi%20Law%20OS%20sales%20team%2C%0A%0AI%27m%20interested%20in%20the%20${encodeURIComponent(proRata.target_plan?.name || "Enterprise")}%20plan.%20Please%20get%20in%20touch%20to%20discuss%20pricing%2C%20SLAs%20and%20onboarding.%0A%0AThanks.`}
                        className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-primary text-primary-foreground text-sm font-semibold hover:opacity-90"
                        data-testid="contact-sales-btn"
                        onClick={cancel}
                    >
                        Contact Sales
                    </a>
                ) : (
                    <GoldButton onClick={confirmChange} disabled={busy || (proRata.is_upgrade && proRata.amount_due_now === 0)} data-testid="confirm-change">
                        {busy ? "Processing…" : (proRata.is_upgrade ? `Pay ₹${proRata.amount_due_now} & upgrade` : "Schedule downgrade")}
                    </GoldButton>
                )}
            </div>
        </div>
    );
}

function HowItWorksTab({ logic, metrics }) {
    if (!logic) return <div className="text-sm text-muted-foreground">Loading…</div>;
    return (
        <div className="space-y-6" data-testid="how-it-works">
            <Section title="Upgrade pro-rata — daily granularity" tone="emerald" icon={TrendingUp}>
                <FormulaBlock items={logic.upgrade.formula}/>
                <ExampleBlock title={logic.upgrade.example.scenario} steps={logic.upgrade.example.steps}/>
                <p className="text-[11px] text-muted-foreground"><span className="text-emerald-400">Takes effect:</span> {logic.upgrade.takes_effect}</p>
            </Section>

            <Section title="Downgrade pro-rata — monthly granularity" tone="blue" icon={TrendingDown}>
                <FormulaBlock items={logic.downgrade.formula}/>
                <ExampleBlock title={logic.downgrade.example.scenario} steps={logic.downgrade.example.steps}/>
                <p className="text-[11px] text-muted-foreground"><span className="text-blue-400">Takes effect:</span> {logic.downgrade.takes_effect}</p>
            </Section>

            <Section title="Plan suggestion engine" tone="gold" icon={Sparkles}>
                <div className="text-xs text-muted-foreground mb-3">{logic.suggestion_engine.evaluation}</div>
                <div className="text-[10px] uppercase tracking-[0.15em] text-muted-foreground mb-1">Metrics tracked</div>
                <div className="flex flex-wrap gap-1 mb-3">
                    {logic.suggestion_engine.metrics_tracked.map(m => (
                        <span key={m} className="text-[10px] px-2 py-1 border border-border bg-card font-mono">
                            {m}{metrics && metrics[m] !== undefined ? <span className="text-primary ml-1">= {String(metrics[m])}</span> : ""}
                        </span>
                    ))}
                </div>
                <div className="text-[10px] uppercase tracking-[0.15em] text-muted-foreground mb-1">Rule shape</div>
                <pre className="text-[10px] font-mono bg-card border border-border p-2 mb-3 overflow-x-auto">{logic.suggestion_engine.rule_shape}</pre>
                <div className="text-[10px] uppercase tracking-[0.15em] text-muted-foreground mb-1">Example rule</div>
                <pre className="text-[10px] font-mono bg-card border border-border p-2 overflow-x-auto">{JSON.stringify(logic.suggestion_engine.example_rule, null, 2)}</pre>
            </Section>

            <Section title="Auditability" tone="gold" icon={Info}>
                <div className="text-[10px] uppercase tracking-[0.15em] text-muted-foreground mb-1">Rows written per change</div>
                <ul className="text-xs text-muted-foreground list-disc list-inside space-y-1 mb-3">
                    {logic.auditability.rows_written_per_change.map((r, i) => <li key={i} className="font-mono">{r}</li>)}
                </ul>
                <div className="text-[10px] uppercase tracking-[0.15em] text-muted-foreground mb-1">Where to view</div>
                <div className="text-xs text-muted-foreground space-y-1">
                    <div><strong className="text-foreground">Tenant:</strong> {logic.auditability.where_to_view.tenant}</div>
                    <div><strong className="text-foreground">Super admin:</strong> {logic.auditability.where_to_view.super_admin}</div>
                </div>
            </Section>
        </div>
    );
}

function Section({ title, tone, icon: Icon, children }) {
    const toneCls = tone === "emerald" ? "border-emerald-700/40" : tone === "blue" ? "border-blue-700/40" : "border-primary/40";
    const iconCls = tone === "emerald" ? "text-emerald-400" : tone === "blue" ? "text-blue-400" : "text-primary";
    return (
        <div className={`border ${toneCls} bg-card p-5`}>
            <div className="flex items-center gap-2 mb-3"><Icon className={`w-4 h-4 ${iconCls}`}/><div className="text-sm font-medium">{title}</div></div>
            {children}
        </div>
    );
}

function FormulaBlock({ items }) {
    return (
        <div className="mb-4">
            <div className="text-[10px] uppercase tracking-[0.15em] text-muted-foreground mb-1">Formula</div>
            <pre className="text-[11px] font-mono bg-background border border-border p-3 overflow-x-auto whitespace-pre-wrap">{items.join("\n")}</pre>
        </div>
    );
}

function ExampleBlock({ title, steps }) {
    return (
        <div className="mb-4">
            <div className="text-[10px] uppercase tracking-[0.15em] text-muted-foreground mb-1">Worked example</div>
            <div className="text-xs text-foreground mb-2">{title}</div>
            <pre className="text-[11px] font-mono bg-background border border-border p-3 overflow-x-auto whitespace-pre-wrap">{steps.join("\n")}</pre>
        </div>
    );
}

function HistoryTab({ items, cancelChange }) {
    const tableCtrl = useTableControls(items, {
        accessors: {
            from_to: (it) => `${it.from_plan} → ${it.to_plan}`,
            amount: (it) => it.kind === "upgrade" ? (it.amount_due_now || 0) : -(it.credit_to_next_invoice || 0),
            initiated_by: (it) => it.initiated_by_email || "",
        },
    });
    if (!items.length) return <div className="text-sm text-muted-foreground p-6 border border-border text-center">No plan changes yet. Pick a plan from <strong>Compare</strong> to begin.</div>;
    return (
        <div className="border border-border overflow-x-auto" data-testid="history-table">
            <table className="w-full text-xs">
                <thead className="bg-muted">
                    <tr>
                        <SortableTh field="created_at" ctrl={tableCtrl} className="px-3 py-2 text-left">When</SortableTh>
                        <SortableTh field="from_to" ctrl={tableCtrl} className="px-3 py-2 text-left">From → To</SortableTh>
                        <SortableTh field="kind" ctrl={tableCtrl} className="px-3 py-2 text-left">Kind</SortableTh>
                        <SortableTh field="amount" ctrl={tableCtrl} className="px-3 py-2 text-left">Amount</SortableTh>
                        <SortableTh field="status" ctrl={tableCtrl} className="px-3 py-2 text-left">Status</SortableTh>
                        <SortableTh field="initiated_by" ctrl={tableCtrl} className="px-3 py-2 text-left">Initiated by</SortableTh>
                        <th className="px-3 py-2 text-left"></th>
                    </tr>
                </thead>
                <tbody>
                    {tableCtrl.rows.map(it => (
                        <tr key={it.id} className="border-t border-border" data-testid={`history-${it.id}`}>
                            <td className="px-3 py-2 text-muted-foreground">{new Date(it.created_at).toLocaleString("en-IN", { timeZone: "Asia/Kolkata", hour12: false })}</td>
                            <td className="px-3 py-2 text-foreground">{it.from_plan} → <strong>{it.to_plan}</strong></td>
                            <td className="px-3 py-2">
                                <span className={`text-[10px] uppercase tracking-[0.1em] px-2 py-1 ${it.kind === "upgrade" ? "bg-emerald-950/40 text-emerald-300" : "bg-blue-950/40 text-blue-300"}`}>{it.kind}</span>
                            </td>
                            <td className="px-3 py-2 font-mono">
                                {it.kind === "upgrade"
                                    ? `₹${(it.amount_due_now || 0).toLocaleString("en-IN", {minimumFractionDigits: 2})}`
                                    : `−₹${(it.credit_to_next_invoice || 0).toLocaleString("en-IN", {minimumFractionDigits: 2})}`}
                            </td>
                            <td className="px-3 py-2">
                                <span className={`text-[10px] uppercase tracking-[0.1em] ${
                                    it.status === "applied" ? "text-emerald-400" :
                                    it.status === "scheduled" || it.status === "pending_payment" ? "text-primary" :
                                    it.status === "cancelled" ? "text-muted-foreground" :
                                    "text-rose-400"
                                }`}>{it.status}</span>
                            </td>
                            <td className="px-3 py-2 text-muted-foreground">{it.initiated_by_email || "—"}</td>
                            <td className="px-3 py-2">
                                {(it.status === "scheduled" || it.status === "pending_payment") && (
                                    <button onClick={() => cancelChange(it.id)} data-testid={`cancel-${it.id}`}
                                        className="text-[10px] uppercase tracking-[0.1em] text-rose-400 hover:text-rose-300">
                                        Cancel
                                    </button>
                                )}
                            </td>
                        </tr>
                    ))}
                </tbody>
            </table>
        </div>
    );
}
